import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Users, Wrench, Calendar, Briefcase, CheckCircle, Phone, Mail, MapPin, DollarSign, ClipboardList, X, FileText, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const positions = [
  {
    id: 'technician',
    title: 'Service Technician',
    icon: Wrench,
    description: 'Experienced appliance repair, plumbing, or electrical technicians',
    requirements: ['Valid driver\'s license', 'Own tools preferred', 'Background check required']
  },
  {
    id: 'contractor',
    title: 'Contractor Partner',
    icon: Briefcase,
    description: 'Licensed contractors looking to partner with RemodWorks',
    requirements: ['Active contractor license', 'Liability insurance', 'Reliable transportation']
  },
  {
    id: 'scheduler',
    title: 'Scheduler / Dispatcher',
    icon: Calendar,
    description: 'Organized individuals to coordinate service appointments',
    requirements: ['Excellent communication skills', 'Computer proficiency', 'Customer service experience']
  }
];

// Contractor payment structure from PDF
const payoutStructure = [
  {
    tier: 'Tier 1 - Appliance Care',
    payout: '$55',
    visits: '2 visits/year',
    scope: 'Inspection, basic cleaning, light adjustments'
  },
  {
    tier: 'Tier 2 - Systems Care',
    payout: '$65',
    visits: '3 visits/year',
    scope: 'Multi-system maintenance coverage'
  },
  {
    tier: 'Tier 3 - Whole Home',
    payout: '$75',
    visits: '4 visits/year',
    scope: 'Comprehensive home coverage'
  }
];

const contractorRules = [
  'Visits are scheduled by RemodWorks',
  'Work is limited to customer\'s maintenance tier scope',
  'Parts are billed at cost when applicable',
  'Repairs outside membership scope require approval',
  'Photos and brief notes required after each visit'
];

// Downloadable forms for contractors (independent contractors)
const contractorForms = [
  {
    name: 'Contractor Maintenance Agreement',
    url: '/Remodworks_Contractor_Agreement.pdf',
    description: 'RemodWorks contractor partnership agreement'
  },
  {
    name: 'W-9 Form',
    url: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf',
    description: 'Request for Taxpayer ID (IRS)'
  },
  {
    name: '1099 Information',
    url: 'https://www.irs.gov/pub/irs-pdf/i1099msc.pdf',
    description: '1099-MISC Instructions (IRS)'
  }
];

// Downloadable forms for employees (technicians, schedulers)
const employeeForms = [
  {
    name: 'W-4 Form',
    url: 'https://www.irs.gov/pub/irs-pdf/fw4.pdf',
    description: 'Employee Withholding Certificate (IRS)'
  },
  {
    name: 'I-9 Form',
    url: 'https://www.uscis.gov/sites/default/files/document/forms/i-9-paper-version.pdf',
    description: 'Employment Eligibility Verification (USCIS)'
  }
];

const benefits = [
  'Competitive pay & bonuses',
  'Flexible scheduling',
  'Steady work flow',
  'Professional growth',
  'Supportive team',
  'Veteran-owned company'
];

export const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePositionChange = (value) => {
    setFormData({
      ...formData,
      position: value
    });
  };

  const openModal = (position) => {
    setSelectedPosition(position);
    setFormData({ ...formData, position: position.id });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPosition(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.position) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/job-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Google Ads conversion tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {'send_to': 'AW-17881958235/PI-fCLWyn_AbENuO5M5C'});
        }
        toast.success('Application submitted! We\'ll review it and get back to you soon.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          message: ''
        });
        closeModal();
      } else {
        toast.error(data.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please email us at support@remodworks.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isContractor = selectedPosition?.id === 'contractor';

  return (
    <section id="careers" className="py-16 sm:py-20 lg:py-24 presidential-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Badge className="mb-4 bg-orange-500/20 text-orange-400 border border-orange-500/50 px-4 py-1.5 text-sm font-semibold">
            <Users className="h-4 w-4 mr-1 inline" />
            NOW HIRING
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
            Join Our <span className="bg-gradient-to-r from-orange-600 via-orange-700 to-amber-800 bg-clip-text text-transparent">Re</span>modCrew
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-3xl mx-auto">
            We're looking for talented technicians, contractors, and schedulers to join our growing team serving Northeast Ohio.
          </p>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          {benefits.map((benefit, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className="bg-slate-800/50 border-orange-500/50 text-orange-400 px-3 py-1.5"
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              {benefit}
            </Badge>
          ))}
        </motion.div>

        {/* Position Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {positions.map((position, index) => (
            <motion.div
              key={position.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card 
                className="h-full bg-slate-800/50 border border-slate-700 hover:border-orange-500/50 transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/10 cursor-pointer"
                data-testid={`position-card-${position.id}`}
                onClick={() => openModal(position)}
              >
                <CardHeader className="pb-4">
                  <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                    <position.icon className="h-6 w-6 text-orange-400" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-white">
                    {position.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-400 mb-4">
                    {position.description}
                  </p>
                  <ul className="space-y-2">
                    {position.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-500">
                        <CheckCircle className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full mt-4 btn-gradient-orange text-white rounded-lg shadow-lg"
                    data-testid={`apply-${position.id}-btn`}
                  >
                    Apply Now
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Application Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border border-slate-700 p-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white p-6 rounded-t-lg sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                    {selectedPosition?.icon && <selectedPosition.icon className="h-6 w-6" />}
                    {selectedPosition?.title || 'Apply Now'}
                  </DialogTitle>
                  <p className="text-sm opacity-90 mt-1">Join the RemodCrew</p>
                </div>
                <button 
                  onClick={closeModal}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Contractor Payment Structure - Only show for contractors */}
              {isContractor && (
                <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-orange-500/30">
                  <h3 className="text-lg font-semibold text-orange-400 mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Contractor Payment Structure
                  </h3>
                  
                  {/* Payout Table */}
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-600">
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Tier</th>
                          <th className="text-center py-2 px-3 text-slate-400 font-medium">Per Visit</th>
                          <th className="text-center py-2 px-3 text-slate-400 font-medium">Visits/Year</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payoutStructure.map((tier, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50">
                            <td className="py-2 px-3 text-white">{tier.tier}</td>
                            <td className="py-2 px-3 text-center">
                              <span className="text-green-400 font-semibold">{tier.payout}</span>
                            </td>
                            <td className="py-2 px-3 text-center text-slate-300">{tier.visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Guidelines */}
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      General Guidelines
                    </h4>
                    <ul className="space-y-1">
                      {contractorRules.map((rule, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-slate-400">
                          <CheckCircle className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />
                          {rule}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-xs text-slate-500 mt-4 italic">
                    * Major repair events include labor capped at stated hours. Parts billed at cost.
                  </p>

                  {/* Contractor Forms Download */}
                  <div className="mt-4 pt-4 border-t border-slate-600">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-400" />
                      Contractor Forms (Download, Fill & Submit)
                    </h4>
                    <div className="space-y-2">
                      {contractorForms.map((form, idx) => (
                        <a
                          key={idx}
                          href={form.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg border border-slate-600 hover:border-orange-500/50 transition-all group"
                          data-testid={`contractor-form-${idx}`}
                        >
                          <Download className="h-4 w-4 text-orange-400 group-hover:text-orange-300" />
                          <div className="flex-1">
                            <span className="text-sm text-slate-300 group-hover:text-white block">{form.name}</span>
                            <span className="text-xs text-slate-500">{form.description}</span>
                          </div>
                          <span className="text-xs text-slate-500">PDF</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Employee Forms - For Technicians and Schedulers */}
              {!isContractor && selectedPosition && (
                <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-blue-500/30">
                  <h3 className="text-lg font-semibold text-blue-400 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Employee Forms (Download, Fill & Submit)
                  </h3>
                  <p className="text-xs text-slate-400 mb-3">
                    Please download and complete these forms along with your resume.
                  </p>
                  <div className="space-y-2">
                    {employeeForms.map((form, idx) => (
                      <a
                        key={idx}
                        href={form.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg border border-slate-600 hover:border-blue-500/50 transition-all group"
                        data-testid={`employee-form-${idx}`}
                      >
                        <Download className="h-4 w-4 text-blue-400 group-hover:text-blue-300" />
                        <div className="flex-1">
                          <span className="text-sm text-slate-300 group-hover:text-white block">{form.name}</span>
                          <span className="text-xs text-slate-500">{form.description}</span>
                        </div>
                        <span className="text-xs text-slate-500">PDF</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-300">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="mt-1.5 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                      data-testid="job-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-300">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="mt-1.5 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                      data-testid="job-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-300">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(234) 205-1010"
                      className="mt-1.5 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                      data-testid="job-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position" className="text-sm font-medium text-slate-300">
                      Position *
                    </Label>
                    <Select value={formData.position} onValueChange={handlePositionChange}>
                      <SelectTrigger className="mt-1.5 bg-slate-800/50 border-slate-600 text-white" data-testid="job-position-select">
                        <SelectValue placeholder="Select a position" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="technician" className="text-white hover:bg-slate-700">Service Technician</SelectItem>
                        <SelectItem value="contractor" className="text-white hover:bg-slate-700">Contractor Partner</SelectItem>
                        <SelectItem value="scheduler" className="text-white hover:bg-slate-700">Scheduler / Dispatcher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience" className="text-sm font-medium text-slate-300">
                    Years of Experience
                  </Label>
                  <Input
                    id="experience"
                    name="experience"
                    type="text"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="e.g., 5 years in appliance repair"
                    className="mt-1.5 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                    data-testid="job-experience-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-slate-300">
                    Tell Us About Yourself
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Share your experience, skills, and why you want to join RemodWorks..."
                    className="mt-1.5 min-h-[100px] bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                    data-testid="job-message-input"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 btn-gradient-orange text-white h-11 font-semibold disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-lg"
                    data-testid="job-submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeModal}
                    className="px-6 h-11 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-700 text-center text-sm text-slate-500">
                <p className="mb-2">Or contact us directly:</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="mailto:support@remodworks.com" className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 hover:underline">
                    <Mail className="h-4 w-4" />
                    support@remodworks.com
                  </a>
                  <a href="tel:+12342051010" className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 hover:underline">
                    <Phone className="h-4 w-4" />
                    (234) 205-1010
                  </a>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Service Area Note */}
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="text-sm text-slate-500">
            <MapPin className="h-4 w-4 inline mr-1" />
            Serving Northeast Ohio • Veteran-Owned Business
          </p>
          <a 
            href="mailto:support@remodworks.com" 
            className="inline-flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300 mt-2"
          >
            <Mail className="h-4 w-4" />
            support@remodworks.com
          </a>
        </motion.div>
      </div>
    </section>
  );
};
